import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useUserRole = (userId: string | undefined) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCoach, setIsCoach] = useState(false);
  const [isAssociate, setIsAssociate] = useState(false);
  const [isAssistant, setIsAssistant] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setIsAdmin(false);
      setIsCoach(false);
      setIsAssociate(false);
      setIsAssistant(false);
      setLoading(false);
      setLoadedForUserId(null);
      return;
    }

    // Reset loading state when userId changes to ensure we wait for fetch
    setLoading(true);

    let cancelled = false;

    const fetchRoles = async () => {
      const [adminResult, coachResult, associateResult, assistantResult] =
        await Promise.all([
          supabase.rpc("is_admin", { _user_id: userId }),
          supabase.rpc("is_coach", { _user_id: userId }),
          supabase.rpc("is_associate", { _user_id: userId }),
          supabase.rpc("is_assistant", { _user_id: userId }),
        ]);

      if (cancelled) return;

      const roleCheckFailed = [
        adminResult,
        coachResult,
        associateResult,
        assistantResult,
      ].some((result) => result.error);

      if (roleCheckFailed) {
        const { data: roles, error } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId);

        if (cancelled) return;

        if (error) throw error;

        setIsAdmin(roles?.some((r) => r.role === "admin") ?? false);
        setIsCoach(roles?.some((r) => r.role === "coach") ?? false);
        setIsAssociate(roles?.some((r) => r.role === "associate") ?? false);
        setIsAssistant(roles?.some((r) => (r.role as string) === "assistant") ?? false);
        setLoadedForUserId(userId);
        setLoading(false);
        return;
      }

      setIsAdmin(adminResult.data === true);
      setIsCoach(coachResult.data === true);
      setIsAssociate(associateResult.data === true);
      setIsAssistant(assistantResult.data === true);
      setLoadedForUserId(userId);
      setLoading(false);
    };

    fetchRoles().catch((error) => {
      if (cancelled) return;
      console.error("[useUserRole] Failed to load roles", error);
      setLoadedForUserId(userId);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { isAdmin, isCoach, isAssociate, isAssistant, loading, loadedForUserId };
};
